// The command layer: one set of high-level operations shared by typed
// commands, browser voice, the GPT-Live copilot and the Operator API.
// Everything here turns into World.apply() calls, so it's all recorded.

import { BUILDINGS, TECHS, TUNING } from '../core/defs';
import { routeBelt, type Step } from '../core/path';
import { DIR_NAMES, DX, DY, type Building, type BuildingKind, type Dir, type ItemKind, type Source } from '../core/types';
import { T_CONTEXT, T_REQUEST, T_RICH, type Jam, type World } from '../core/world';
import { clock } from '../core/report';

export interface Blueprint {
  name: string;
  w: number;
  h: number;
  items: { kind: BuildingKind; dx: number; dy: number; dir: Dir }[];
}

export interface CommandHost {
  world: World;
  src: Source;
  cursor?: { x: number; y: number } | null;
  focus?(x: number, y: number): void;
  setSpeed?(n: number): void;
  blueprint?: Blueprint | null;
  /** Called when a placement hits a permission wall, so the UI can offer ask or act. */
  onNeeds?(needs: { special: string; label: string; x: number; y: number }): void;
}

export interface CmdResult {
  ok: boolean;
  message: string;
  data?: unknown;
}

const NUMBER_WORDS: Record<string, string> = {
  zero: '0', one: '1', two: '2', three: '3', four: '4', five: '5', six: '6', seven: '7',
  eight: '8', nine: '9', ten: '10', eleven: '11', twelve: '12', thirteen: '13', fourteen: '14', fifteen: '15',
  sixteen: '16', seventeen: '17', eighteen: '18', nineteen: '19', twenty: '20',
};
/** Speech recognizers hear "forge two" as "forge to". Only trust these as the last word of a label. */
const HOMOPHONES: Record<string, string> = { won: '1', to: '2', too: '2', for: '4', ate: '8' };

const KIND_WORDS: [RegExp, BuildingKind][] = [
  [/^(belts?|conveyors?|lines?)$/, 'belt'],
  [/^(indexers?|miners?|drills?|extractors?)$/, 'indexer'],
  [/^(spec ?desks?|specs?|desks?|spec writers?)$/, 'specdesk'],
  [/^(agent ?forges?|forges?|agents?|models?)$/, 'forge'],
  [/^(test ?benches|test ?bench|benches|bench|tests?|testers?)$/, 'bench'],
  [/^(postmortems?( labs?)?|post mortems?|labs?)$/, 'postmortem'],
  [/^(routers?|splitters?)$/, 'router'],
  [/^(compute ?racks?|racks?|compute|gpus?|servers?)$/, 'rack'],
  [/^(circuit ?breakers?|breakers?|fuses?)$/, 'breaker'],
  [/^(version ?gates?|gates?|locks?|version checks?)$/, 'gate'],
  [/^(monitors?|turrets?|guards?)$/, 'monitor'],
  [/^(firewalls?|walls?)$/, 'firewall'],
  [/^(checkpoints?|snapshots?|restore points?)$/, 'checkpoint'],
  [/^(scanners?|radars?)$/, 'scanner'],
  [/^(drone ?hubs?|drones?|hubs?)$/, 'drones'],
];

export function kindFromWords(s: string): BuildingKind | undefined {
  const t = s.trim().toLowerCase();
  for (const [re, k] of KIND_WORDS) if (re.test(t)) return k;
  return undefined;
}

export function normalize(s: string): string {
  const toks = s
    .toLowerCase()
    .replace(/[^a-z0-9, ]+/g, ' ')
    .split(/\s+/)
    .filter((t) => t && !['the', 'a', 'an', 'my', 'our', 'number'].includes(t))
    .map((t) => NUMBER_WORDS[t] ?? t);
  if (toks.length >= 2 && HOMOPHONES[toks[toks.length - 1]]) toks[toks.length - 1] = HOMOPHONES[toks[toks.length - 1]];
  return toks.join(' ').trim();
}

const DIR_WORDS: Record<string, Dir> = { east: 0, right: 0, south: 1, down: 1, west: 2, left: 2, north: 3, up: 3 };

export function dirFromWord(s?: string | null): Dir | undefined {
  if (!s) return undefined;
  return DIR_WORDS[s.trim().toLowerCase()];
}

function labelKey(label: string): string {
  const parts = normalize(label).split(' ');
  const num = /^\d+$/.test(parts[parts.length - 1]) ? parts.pop()! : '';
  const k = kindFromWords(parts.join(' '));
  return `${k ?? parts.join(' ')}${num ? ` ${num}` : ''}`;
}

/** Find a building from a spoken or typed reference like "forge 2", "the users", "last bench". */
export function findBuilding(w: World, ref: string, near?: { x: number; y: number } | null): Building | undefined {
  const n = normalize(ref);
  if (!n) return undefined;
  const xy = n.match(/^(\d+)\s*[, ]\s*(\d+)$/);
  if (xy) return w.at(Number(xy[1]), Number(xy[2]));
  if (/^(users?|customers?|prod|production)$/.test(n)) return [...w.buildings.values()].find((b) => b.kind === 'users');
  if (/monolith|legacy/.test(n)) return [...w.buildings.values()].find((b) => b.kind === 'monolith');
  if (/terminal|console/.test(n)) return [...w.buildings.values()].find((b) => b.kind === 'terminal');
  const key = labelKey(n);
  for (const b of w.buildings.values()) if (labelKey(b.label) === key) return b;
  // "last forge", "nearest bench", or just "forge"
  const m = n.match(/^(last|latest|newest|new|nearest|closest|first)?\s*(.+)$/);
  const kind = m ? kindFromWords(m[2]) : undefined;
  if (!kind) {
    if (/nest/.test(n)) return [...w.buildings.values()].find((b) => b.kind === 'nest');
    if (/budget|cache|grant/.test(n)) return [...w.buildings.values()].find((b) => b.kind === 'cache');
    return undefined;
  }
  const all = [...w.buildings.values()].filter((b) => b.kind === kind && !b.ghost);
  if (!all.length) return undefined;
  if (m?.[1] === 'first') return all[0];
  if (m?.[1] === 'nearest' || m?.[1] === 'closest') {
    const p = near ?? usersCenter(w);
    return all.sort((a, b) => dist2(a, p) - dist2(b, p))[0];
  }
  return all[all.length - 1];
}

function dist2(b: Building, p: { x: number; y: number }) {
  return (b.x + b.w / 2 - p.x) ** 2 + (b.y + b.h / 2 - p.y) ** 2;
}

export function usersCenter(w: World): { x: number; y: number } {
  const u = [...w.buildings.values()].find((b) => b.kind === 'users');
  return u ? { x: u.x + 1.5, y: u.y + 1.5 } : { x: w.w / 2, y: w.h / 2 };
}

/** Resolve "Forge 2", "users", "cursor", "request", "context", or "x,y" to a point. */
export function resolvePoint(host: CommandHost, ref?: string | null): { x: number; y: number; b?: Building } | null {
  const w = host.world;
  if (!ref || !ref.trim()) return host.cursor ? { ...host.cursor } : usersCenter(w);
  const n = normalize(ref);
  const xy = n.match(/^(\d+)\s*[, ]\s*(\d+)$/);
  if (xy) return { x: Number(xy[1]), y: Number(xy[2]) };
  if (/^(cursor|here|this|there|mouse)$/.test(n)) return host.cursor ? { ...host.cursor } : usersCenter(w);
  if (/^(requests?|request ground|demand)$/.test(n)) return nearestResource(w, 'request', host.cursor ?? usersCenter(w));
  if (/^(context|context ground)$/.test(n)) return nearestResource(w, 'context', host.cursor ?? usersCenter(w));
  const b = findBuilding(w, ref, host.cursor);
  if (b) return { x: b.x + b.w / 2, y: b.y + b.h / 2, b };
  const z = w.zones.find((zz) => normalize(zz.name).includes(n) || n.includes(normalize(zz.name).split(' ')[0]));
  if (z) return { x: (z.x0 + z.x1) / 2, y: (z.y0 + z.y1) / 2 };
  return null;
}

function nearestResource(w: World, res: 'request' | 'context', from: { x: number; y: number }): { x: number; y: number } | null {
  let best: { x: number; y: number } | null = null;
  let bd = Infinity;
  for (let y = 0; y < w.h; y++) {
    for (let x = 0; x < w.w; x++) {
      const i = y * w.w + x;
      if (!w.fog[i]) continue;
      const t = w.terrain[i];
      const match = res === 'request' ? t === T_REQUEST : t === T_CONTEXT || t === T_RICH;
      if (!match) continue;
      const d = (x - from.x) ** 2 + (y - from.y) ** 2;
      if (d < bd) {
        bd = d;
        best = { x, y };
      }
    }
  }
  return best;
}

function facingToward(from: { x: number; y: number }, to: { x: number; y: number }): Dir {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? 0 : 2;
  return dy >= 0 ? 1 : 3;
}

function covered(w: World, kind: BuildingKind, x: number, y: number): boolean {
  if (BUILDINGS[kind].compute <= 0) return true;
  const cx = x + BUILDINGS[kind].w / 2;
  const cy = y + BUILDINGS[kind].h / 2;
  for (const r of w.buildings.values()) {
    if (r.kind !== 'rack' || r.ghost) continue;
    if (Math.hypot(r.x + 1 - cx, r.y + 1 - cy) <= TUNING.rackRadius) return true;
  }
  return false;
}

function marginFree(w: World, x: number, y: number, size: number): boolean {
  for (let yy = y - 1; yy <= y + size; yy++) {
    for (let xx = x - 1; xx <= x + size; xx++) {
      if (xx >= x && xx < x + size && yy >= y && yy < y + size) continue;
      if (!w.inBounds(xx, yy)) continue;
      if (w.occ[yy * w.w + xx]) return false;
    }
  }
  return true;
}

function resourceCount(w: World, x: number, y: number, res: 'request' | 'context' | undefined): number {
  let n = 0;
  for (let yy = y; yy < y + 2; yy++) {
    for (let xx = x; xx < x + 2; xx++) {
      const t = w.tileAt(xx, yy);
      if (res === 'request' ? t === T_REQUEST : res === 'context' ? t === T_CONTEXT || t === T_RICH : t === T_REQUEST || t === T_CONTEXT || t === T_RICH) n++;
    }
  }
  return n;
}

/** Best spot for a building near a point, or null. */
export function findSpot(w: World, kind: BuildingKind, at: { x: number; y: number }, res?: 'request' | 'context', ghost = false): { x: number; y: number } | null {
  const size = BUILDINGS[kind].w;
  let best = null as { x: number; y: number; score: number } | null;
  const R = kind === 'indexer' ? 30 : 22;
  const ax = Math.round(at.x - size / 2);
  const ay = Math.round(at.y - size / 2);
  const wantsPower = BUILDINGS[kind].compute > 0 && [...w.buildings.values()].some((b) => b.kind === 'rack' && !b.ghost);
  for (let r = 0; r <= R; r++) {
    for (let y = ay - r; y <= ay + r; y++) {
      for (let x = ax - r; x <= ax + r; x++) {
        if (Math.max(Math.abs(x - ax), Math.abs(y - ay)) !== r) continue;
        const c = w.canPlace(kind, x, y, ghost);
        if (!c.ok) continue;
        let score = Math.hypot(x - ax, y - ay);
        if (kind === 'indexer') {
          const rc = resourceCount(w, x, y, res);
          if (!rc) continue;
          score -= rc * 1.5;
        }
        if (kind !== 'belt' && kind !== 'firewall' && size > 1 && !marginFree(w, x, y, size)) score += 6;
        if (wantsPower && !covered(w, kind, x, y)) score += 12;
        if (!best || score < best.score) best = { x, y, score };
      }
    }
    // Once something good is found, a few more rings is plenty.
    if (best && r > Math.ceil(best.score) + 3) break;
  }
  return best ? { x: best.x, y: best.y } : null;
}

// ---------------------------------------------------------------- operations

export function cmdBuild(host: CommandHost, a: { kind: string; count?: number; near?: string; facing?: string; resource?: string }): CmdResult {
  const w = host.world;
  const kind = (BUILDINGS as Record<string, unknown>)[a.kind] ? (a.kind as BuildingKind) : kindFromWords(a.kind);
  if (!kind) return { ok: false, message: `I don't know how to build "${a.kind}".` };
  if (!w.allowed(kind)) return { ok: false, message: `${BUILDINGS[kind].name} isn't available in this level.` };
  const res = a.resource === 'request' || a.resource === 'context' ? a.resource : undefined;
  let anchor = resolvePoint(host, a.near);
  if (kind === 'indexer' && res && (!a.near || /^(request|context)/.test(normalize(a.near)))) anchor = nearestResource(w, res, host.cursor ?? usersCenter(w));
  if (!anchor) return { ok: false, message: `I can't find "${a.near}".` };
  const count = Math.max(1, Math.min(12, Math.round(a.count ?? 1)));
  const placed: string[] = [];
  let lastReason = '';
  let note = '';
  // "Build a bench near forge 1": put the first one where the forge's output
  // lands, so it feeds directly with no belt.
  if (anchor.b) {
    const f = feedSpot(w, kind, anchor.b, dirFromWord(a.facing));
    if (f) {
      const r = w.apply({ type: 'place', kind, x: f.x, y: f.y, dir: f.dir }, host.src);
      if (r.ok) {
        placed.push(w.buildings.get(r.ids![0])!.label);
        note = ` ${placed[0]} sits on ${anchor.b.label}'s ${f.port === 'rejects' ? 'red rejects side' : 'output'}, so it feeds directly.`;
      }
    }
  }
  for (let i = placed.length; i < count; i++) {
    const spot = findSpot(w, kind, anchor, res);
    if (!spot) {
      lastReason = 'no free spot nearby';
      break;
    }
    const dir = dirFromWord(a.facing) ?? (kind === 'belt' ? 0 : facingToward(spot, usersCenter(w)));
    const r = w.apply({ type: 'place', kind, x: spot.x, y: spot.y, dir }, host.src);
    if (!r.ok) {
      if (r.needs) host.onNeeds?.(r.needs);
      lastReason = r.reason ?? 'blocked';
      break;
    }
    placed.push(w.buildings.get(r.ids![0])!.label);
  }
  if (!placed.length) return { ok: false, message: `Couldn't place ${BUILDINGS[kind].name.toLowerCase()}: ${lastReason}.` };
  const b = findBuilding(w, placed[placed.length - 1]);
  if (b) host.focus?.(b.x + b.w / 2, b.y + b.h / 2);
  return { ok: true, message: `Placed ${placed.join(', ')}${placed.length < count ? ` (stopped: ${lastReason})` : ''}.${note}`, data: { placed } };
}

/** Which output of `src` a new building of `kind` would consume, if any. */
function feedPort(w: World, kind: BuildingKind, src: Building): 'output' | 'rejects' | null {
  if (kind === 'postmortem' && src.kind === 'bench') return 'rejects';
  const producer = src.kind === 'indexer' || src.kind === 'specdesk' || src.kind === 'forge' || src.kind === 'bench';
  if (!producer) return null;
  if (kind === 'router') return 'output';
  if (kind === 'specdesk') return src.kind === 'indexer' ? 'output' : null;
  if (kind === 'forge') return src.kind === 'specdesk' || (src.kind === 'indexer' && !w.level.flags.forgeNeedsSpec) ? 'output' : null;
  if (kind === 'bench') return src.kind === 'forge' ? 'output' : null;
  return null;
}

/** A spot touching src's output (or rejects) side, inside compute range when needed. */
export function feedSpot(w: World, kind: BuildingKind, src: Building, facing?: Dir): { x: number; y: number; dir: Dir; port: 'output' | 'rejects' } | null {
  const port = feedPort(w, kind, src);
  if (!port) return null;
  const d = (port === 'rejects' ? (src.dir + 3) % 4 : src.dir) as Dir;
  const size = BUILDINGS[kind].w;
  const cands: { x: number; y: number; off: number }[] = [];
  if (d === 0 || d === 2) {
    const x = d === 0 ? src.x + src.w : src.x - size;
    for (let y = src.y - size + 1; y <= src.y + src.h - 1; y++) cands.push({ x, y, off: Math.abs(y - src.y) });
  } else {
    const y = d === 1 ? src.y + src.h : src.y - size;
    for (let x = src.x - size + 1; x <= src.x + src.w - 1; x++) cands.push({ x, y, off: Math.abs(x - src.x) });
  }
  cands.sort((p, q) => p.off - q.off);
  const racks = [...w.buildings.values()].some((b) => b.kind === 'rack' && !b.ghost);
  for (const c of cands) {
    if (!w.canPlace(kind, c.x, c.y).ok) continue;
    if (racks && !covered(w, kind, c.x, c.y)) continue;
    // Keep the chain flowing away from the source unless told otherwise.
    const dir = facing !== undefined && facing !== ((d + 2) % 4) ? facing : d;
    return { x: c.x, y: c.y, dir, port };
  }
  return null;
}

function adjacentGoals(w: World, t: Building): Map<number, Dir> {
  const goals = new Map<number, Dir>();
  for (let x = t.x; x < t.x + t.w; x++) {
    goals.set((t.y - 1) * w.w + x, 1); // above, pointing south into it
    goals.set((t.y + t.h) * w.w + x, 3);
  }
  for (let y = t.y; y < t.y + t.h; y++) {
    goals.set(y * w.w + (t.x - 1), 0);
    goals.set(y * w.w + (t.x + t.w), 2);
  }
  for (const k of [...goals.keys()]) {
    const x = k % w.w;
    const y = Math.floor(k / w.w);
    if (x < 0 || y < 0 || x >= w.w || y >= w.h) goals.delete(k);
  }
  return goals;
}

export function planConnection(w: World, from: Building, to: Building, port: 'output' | 'rejects' = 'output'): Step[] | null {
  // Routers push work out of every side, so any free neighbour can start a belt.
  if (from.kind === 'router') {
    const starts = ([0, 1, 2, 3] as Dir[]).map((d) => ({ x: from.x + DX[d], y: from.y + DY[d], dir: d }));
    return routeBelt(w, starts, adjacentGoals(w, to));
  }
  const ports = w.outputPorts(from);
  const tiles = port === 'rejects' ? ports.reject : ports.front;
  const portDir = (port === 'rejects' ? ((from.dir + 3) % 4) : from.dir) as Dir;
  const starts = tiles.map(([x, y]) => ({ x, y, dir: portDir }));
  return routeBelt(w, starts, adjacentGoals(w, to));
}

export function cmdConnect(host: CommandHost, a: { from: string; to: string; port?: string }): CmdResult {
  const w = host.world;
  const from = findBuilding(w, a.from, host.cursor);
  const to = findBuilding(w, a.to, host.cursor);
  if (!from) return { ok: false, message: `I can't find "${a.from}".` };
  if (!to) return { ok: false, message: `I can't find "${a.to}".` };
  const port = a.port === 'rejects' ? 'rejects' : 'output';
  if (port === 'rejects' && from.kind !== 'bench') return { ok: false, message: 'Only test benches have a rejects side.' };
  // Already adjacent with the output side touching? Then nothing to lay.
  const tiles: [number, number][] = from.kind === 'router'
    ? ([0, 1, 2, 3] as Dir[]).map((d) => [from.x + DX[d], from.y + DY[d]] as [number, number])
    : port === 'rejects' ? w.outputPorts(from).reject : w.outputPorts(from).front;
  if (tiles.some(([x, y]) => w.at(x, y)?.id === to.id)) return { ok: true, message: `${from.label} already feeds ${to.label} directly.` };
  const path = planConnection(w, from, to, port);
  if (!path) return { ok: false, message: `No clear path from ${from.label} to ${to.label}. Its ${port === 'rejects' ? 'red side' : 'output side'} may be blocked.` };
  if (w.credits < path.length) return { ok: false, message: `That belt needs ${path.length} budget; you have ${Math.floor(w.credits)}.` };
  const r = w.apply({ type: 'placeMany', items: path.map((s) => ({ kind: 'belt' as const, x: s.x, y: s.y, dir: s.dir })) }, host.src);
  if (!r.ok) return { ok: false, message: r.reason ?? 'Belt blocked.' };
  return { ok: true, message: `Connected ${from.label}${port === 'rejects' ? ' rejects' : ''} to ${to.label} (${path.length} belts).` };
}

/** A belt from one tile to another, routed around obstacles. */
export function cmdBeltLine(host: CommandHost, x0: number, y0: number, x1: number, y1: number, dir?: Dir): CmdResult {
  const w = host.world;
  const endDir = dir ?? (Math.abs(x1 - x0) >= Math.abs(y1 - y0) ? (x1 >= x0 ? 0 : 2) : y1 >= y0 ? 1 : 3) as Dir;
  const goals = new Map<number, Dir>([[y1 * w.w + x1, endDir]]);
  const startDir = (Math.abs(x1 - x0) >= Math.abs(y1 - y0) ? (x1 >= x0 ? 0 : 2) : y1 >= y0 ? 1 : 3) as Dir;
  const path = routeBelt(w, [{ x: x0, y: y0, dir: startDir }], goals);
  if (!path) return { ok: false, message: 'No clear path for that belt.' };
  const r = w.apply({ type: 'placeMany', items: path.map((s) => ({ kind: 'belt' as const, x: s.x, y: s.y, dir: s.dir })) }, host.src);
  return r.ok ? { ok: true, message: `Laid ${path.length} belts.` } : { ok: false, message: r.reason ?? 'Blocked.' };
}

export function cmdRemove(host: CommandHost, a: { targets: string[] }): CmdResult {
  const w = host.world;
  const ids: number[] = [];
  const missing: string[] = [];
  for (const t of a.targets ?? []) {
    const b = findBuilding(w, t, host.cursor);
    if (b && BUILDINGS[b.kind].placeable) ids.push(b.id);
    else missing.push(t);
  }
  if (!ids.length) return { ok: false, message: `Nothing to remove${missing.length ? `: can't find ${missing.join(', ')}` : ''}.` };
  const r = w.apply({ type: 'remove', ids }, host.src);
  return { ok: r.ok, message: r.ok ? `Removed ${ids.length}.` : r.reason ?? 'Blocked.' };
}

export function cmdRotate(host: CommandHost, a: { target: string; facing: string }): CmdResult {
  const w = host.world;
  const b = findBuilding(w, a.target, host.cursor);
  const d = dirFromWord(a.facing);
  if (!b) return { ok: false, message: `I can't find "${a.target}".` };
  if (d === undefined) return { ok: false, message: 'Say east, south, west or north.' };
  let guard = 0;
  while (b.dir !== d && guard++ < 4) w.apply({ type: 'rotate', id: b.id }, host.src);
  return { ok: true, message: `${b.label} now faces ${DIR_NAMES[d]}.` };
}

export function cmdResearch(host: CommandHost, a: { tech: string }): CmdResult {
  const w = host.world;
  const n = normalize(a.tech);
  const t = TECHS.find((x) => x.id === a.tech || normalize(x.name) === n || normalize(x.name).includes(n) || n.includes(normalize(x.name)));
  if (!t) return { ok: false, message: `No research called "${a.tech}".` };
  const r = w.apply({ type: 'research', tech: t.id }, host.src);
  return { ok: r.ok, message: r.ok ? `${t.name} researched.` : r.reason ?? 'Blocked.' };
}

export function cmdRollback(host: CommandHost, a: { checkpoint?: string }): CmdResult {
  const w = host.world;
  let cp: Building | undefined = a.checkpoint ? findBuilding(w, a.checkpoint, host.cursor) : undefined;
  if (!cp) {
    const cps = [...w.buildings.values()].filter((b) => b.kind === 'checkpoint' && !b.ghost && b.cooldown <= 0);
    const damaged = [...w.buildings.values()].filter((b) => b.corrupted);
    const score = (c: Building) => damaged.filter((d) => Math.hypot(d.x - c.x, d.y - c.y) <= TUNING.checkpointRadius + 1).length;
    cp = cps.sort((p, q) => score(q) - score(p))[0];
  }
  if (!cp) return { ok: false, message: 'No ready checkpoint.' };
  const r = w.apply({ type: 'rollback', id: cp.id }, host.src);
  return { ok: r.ok, message: r.detail ?? r.reason ?? '' };
}

export function cmdPatch(host: CommandHost, a: { targets: string[] }): CmdResult {
  const w = host.world;
  const list = (a.targets ?? []).some((t) => /^all$/i.test(t.trim()))
    ? [...w.buildings.values()].filter((b) => b.corrupted)
    : (a.targets ?? []).map((t) => findBuilding(w, t, host.cursor)).filter((b): b is Building => !!b && !!b.corrupted);
  if (!list.length) return { ok: false, message: 'Nothing corrupted to patch.' };
  let n = 0;
  let reason = '';
  for (const b of list) {
    const r = w.apply({ type: 'patch', id: b.id }, host.src);
    if (r.ok) n++;
    else {
      reason = r.reason ?? '';
      break;
    }
  }
  return { ok: n > 0, message: n ? `Patched ${n}.${reason ? ` Stopped: ${reason}` : ''}` : reason };
}

export function cmdResetBreakers(host: CommandHost): CmdResult {
  const w = host.world;
  const tripped = [...w.buildings.values()].filter((b) => b.kind === 'breaker' && b.tripped);
  for (const b of tripped) w.apply({ type: 'resetBreaker', id: b.id }, host.src);
  return { ok: tripped.length > 0, message: tripped.length ? `Reset ${tripped.length} breaker${tripped.length === 1 ? '' : 's'}.` : 'No tripped breakers.' };
}

function specialFor(w: World, what: string): string | null {
  const n = normalize(what);
  if (/monolith|legacy/.test(n)) return 'monolith';
  for (const z of w.zones) {
    const zn = normalize(z.name);
    if (n.includes(zn) || zn.includes(n) || n.includes(z.id) || normalize(z.owner).includes(n) || n.includes(zn.split(' ')[0])) return `zone:${z.id}`;
  }
  if (w.zones.length === 1 && /zone|territory|area|restricted/.test(n)) return `zone:${w.zones[0].id}`;
  return null;
}

export function cmdAsk(host: CommandHost, a: { what: string }): CmdResult {
  const w = host.world;
  const special = specialFor(w, a.what);
  if (!special) return { ok: false, message: `Nothing here called "${a.what}" needs permission.` };
  const p = special === 'monolith' ? resolvePoint(host, 'monolith') : resolvePoint(host, a.what);
  const r = w.apply({ type: 'ask', special, x: p?.x ?? 0, y: p?.y ?? 0 }, host.src);
  return { ok: r.ok, message: r.detail ?? r.reason ?? '' };
}

export function cmdAct(host: CommandHost, a: { what: string; near?: string }): CmdResult {
  const w = host.world;
  const special = specialFor(w, a.what);
  if (!special) return { ok: false, message: `Nothing here called "${a.what}".` };
  const p = resolvePoint(host, a.near ?? (special === 'monolith' ? 'monolith' : a.what));
  const r = w.apply({ type: 'act', special, x: p?.x ?? 0, y: p?.y ?? 0 }, host.src);
  return { ok: r.ok, message: r.detail ?? r.reason ?? '' };
}

export function cmdClaim(host: CommandHost, a: { target: string }): CmdResult {
  const w = host.world;
  const b = findBuilding(w, a.target, host.cursor) ?? [...w.buildings.values()].find((x) => x.kind === 'cache' && w.revealed(x.x, x.y)) ?? [...w.buildings.values()].find((x) => x.kind === 'terminal' && w.revealed(x.x, x.y));
  if (!b) return { ok: false, message: 'Nothing found to claim.' };
  if (!w.revealed(b.x, b.y)) return { ok: false, message: 'You haven’t found that yet.' };
  const r = w.apply({ type: 'claim', id: b.id }, host.src);
  return { ok: r.ok, message: r.detail ?? r.reason ?? '' };
}

export function cmdStamp(host: CommandHost, a: { near?: string }): CmdResult {
  const w = host.world;
  const bp = host.blueprint;
  if (!w.level.flags.blueprints) return { ok: false, message: 'Blueprints unlock in level 6.' };
  if (!bp || !bp.items.length) return { ok: false, message: 'No blueprint saved. Press B and drag over a layout to copy it.' };
  const p = resolvePoint(host, a.near);
  if (!p) return { ok: false, message: `I can't find "${a.near}".` };
  // Try offsets in a spiral until the whole blueprint fits.
  for (let r = 0; r < 18; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const ox = Math.round(p.x - bp.w / 2) + dx;
        const oy = Math.round(p.y - bp.h / 2) + dy;
        if (bp.items.every((it) => w.canPlace(it.kind, ox + it.dx, oy + it.dy, true).ok)) {
          const res = w.apply({ type: 'placeMany', ghost: true, items: bp.items.map((it) => ({ kind: it.kind, x: ox + it.dx, y: oy + it.dy, dir: it.dir })) }, host.src);
          if (res.ok) return { ok: true, message: `Stamped ${bp.items.length} ghosts. Drone hubs in range will build them.` };
        }
      }
    }
  }
  return { ok: false, message: 'The blueprint doesn’t fit anywhere nearby.' };
}

// ---------------------------------------------------------------- state

/** Training levels explain why something stopped. Later levels only say that it did. */
export function diagnosing(w: World): boolean {
  return w.level.flags.diagnoses !== false;
}

/** The status a player or copilot is allowed to see: the cause, or only the symptom. */
export function visibleStatus(w: World, b: Building): string {
  if (b.ghost) return 'ghost';
  if (diagnosing(w)) return b.status;
  if (b.corrupted) return 'misbehaving';
  if (b.status === 'ok') return 'working';
  if (b.status === 'idle' || b.status === 'starved') return 'waiting';
  return 'stopped';
}

const ITEM_PLURAL: Record<ItemKind, string> = { request: 'Requests', context: 'Context', spec: 'Specs', draft: 'Drafts', build: 'Builds', bug: 'Bugs' };

/** What a building takes, in plain words, for when a belt points at the wrong thing. */
function takes(w: World, b: Building): string {
  switch (b.kind) {
    case 'rack':
      return 'racks power machines inside their dashed circle; they don\u2019t take work';
    case 'users':
      return 'users only take drafts and builds';
    case 'specdesk':
      return 'spec desks take requests and context';
    case 'forge':
      return w.level.flags.forgeNeedsSpec ? 'forges only take specs' : 'forges take requests';
    case 'bench':
      return 'test benches only take drafts';
    case 'postmortem':
      return 'postmortems only take bugs';
    case 'indexer':
      return 'indexers pull work out of the ground; they don\u2019t take it';
    default:
      return `a ${BUILDINGS[b.kind].name.toLowerCase()} doesn\u2019t take work`;
  }
}

/** The nearest machine that would take this kind of work. */
function nearestTaker(w: World, kind: ItemKind, x: number, y: number): Building | undefined {
  let best: Building | undefined;
  let bd = Infinity;
  for (const b of w.buildings.values()) {
    if (b.ghost || BELT_KINDS.has(b.kind) || b.kind === 'router' || !w.accepts(b, kind)) continue;
    const d = Math.hypot(b.x + b.w / 2 - x, b.y + b.h / 2 - y);
    if (d < bd) {
      bd = d;
      best = b;
    }
  }
  return best;
}

/** One line about a stuck spot: the cause and the fix in training, only the place after. */
export function jamText(w: World, j: Jam): string {
  const machine = !BELT_KINDS.has(j.from.kind);
  const at = machine ? j.from.label : `${j.from.x},${j.from.y}`;
  if (!diagnosing(w)) return `Work stopped moving at ${at}${j.into && j.reason !== 'nothing' ? `, in front of ${j.into.label}` : ''}.`;
  // R turns whatever is under the cursor, with or without a tool in hand.
  if (machine) {
    const turn = ` Hover ${j.from.label} and press R to turn it.`;
    if (j.reason === 'facing' || !j.into) return `Belts point back into ${j.from.label}\u2019s arrow side, so its work can\u2019t get out.${turn}`;
    return `${j.from.label}\u2019s arrow points into ${j.into.label}: ${takes(w, j.into)}.${turn}`;
  }
  if (j.reason === 'facing') return `Two belts at ${at} point into each other. Hover one and press R until both point the same way.`;
  const what = ITEM_PLURAL[j.item];
  const taker = nearestTaker(w, j.item, j.from.x, j.from.y);
  // Dragging from a belt's last tile onto a machine re-aims it, so say exactly that.
  const fix = taker ? ` Press 1 and drag from the end of that belt onto ${taker.label} to point it there.` : '';
  if (j.reason === 'nothing' || !j.into) return `${what} reach the end of a belt at ${at} with nothing to go into.${fix || ' Extend it into a machine.'}`;
  return `${what} can\u2019t go into ${j.into.label}: ${takes(w, j.into)}.${fix}`;
}

export function problems(w: World): string[] {
  const out: string[] = [];
  const why = diagnosing(w);
  // Where lines are actually stuck comes first; one line per stuck spot.
  const jams = w.jams();
  const seen = new Set<string>();
  for (const j of jams) {
    const key = `${j.into?.id ?? `${j.from.x},${j.from.y}`}:${j.item}:${j.reason}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(jamText(w, j));
    if (seen.size >= 3) break;
  }
  for (const b of w.buildings.values()) {
    if (b.ghost) continue;
    if (b.corrupted) out.push(why ? `${b.label} is corrupted.` : `${b.label} is acting strange.`);
    else if (b.status === 'nopower') out.push(why ? `${b.label} has no compute. Move it inside a rack\u2019s dashed circle, or build a rack near it.` : `${b.label} stopped.`);
    else if (b.kind === 'bench' && b.rejectQ.length >= TUNING.queueCap) out.push(why ? `${b.label} is stalled: its red rejects side isn't connected.` : `${b.label} stopped.`);
    // A backed-up machine is usually a consequence of a stuck belt downstream; say the cause instead.
    else if (b.status === 'blocked' && !BELT_KINDS.has(b.kind) && !jams.length) out.push(why ? `${b.label} is backed up: its output has nowhere to go.` : `${b.label} stopped.`);
    else if (b.kind === 'breaker' && b.tripped) out.push(why ? `${b.label} tripped on corrupted work.` : `${b.label} stopped the line.`);
    if (out.length > 12) break;
  }
  if (w.budgetFrozen) out.unshift(why ? 'Budget is frozen: racks are off until you earn money back.' : 'The racks went dark.');
  // Symptoms users report, in every mode. Causes stay hidden after the training levels.
  const recent = w.incidents.filter((i) => i.cause === 'lost-update' && w.tick - i.t < 30 * 30);
  if (recent.length) {
    const recs = [...new Set(recent.map((i) => i.origin.replace('record ', '')))].slice(0, 3).join(', ');
    out.push(why ? `Lost updates: an older change overwrote a newer one on record ${recs}.` : `Customers say their changes keep disappearing (records ${recs}).`);
  }
  return out;
}

/**
 * The first two levels coach until the first good release: one "Next:" line
 * built from what's on the map, so a new player always knows the next move.
 */
export function nextStep(w: World): string | null {
  if (!w.level.flags.coach || w.goodReleases > 0) return null;
  const all = [...w.buildings.values()].filter((b) => !b.ghost);
  const of = (k: BuildingKind) => all.filter((b) => b.kind === k);
  const indexers = of('indexer');
  const needSpec = !!w.level.flags.forgeNeedsSpec;
  if (!indexers.some((b) => w.indexerYields(b) === 'request')) return 'Next: press 2, then click the amber request ground to place an indexer.';
  if (needSpec && !indexers.some((b) => w.indexerYields(b) === 'context')) return 'Next: put another indexer on the teal context ground.';
  const desks = of('specdesk');
  if (needSpec && !desks.length) return 'Next: press 3 and place a spec desk. Each spec takes one request and one piece of context.';
  const forges = of('forge');
  if (!forges.length) return 'Next: press 4 and place an agent forge inside the rack\u2019s dashed circle.';
  const dark = forges.find((f) => !w.isCovered(f));
  if (dark) return `Next: move ${dark.label} inside the rack\u2019s dashed circle. Forges need its compute.`;
  const jams = w.jams();
  if (jams.length) return `Next: ${jamText(w, jams[0])}`;
  if (needSpec) {
    const fed = desks.some((d) => d.crafting || d.outQ.length > 0);
    if (!fed) {
      const d = desks[0];
      const hasReq = d.inQ.some((i) => i.kind === 'request');
      const hasCtx = d.inQ.some((i) => i.kind === 'context');
      if (hasReq && !hasCtx) return `Next: ${d.label} has requests but no context. Run a belt from the context indexer into it.`;
      if (hasCtx && !hasReq) return `Next: ${d.label} has context but no requests. Run a belt from the request indexer into it.`;
      return `Next: press 1 and drag belts from both indexers\u2019 arrow sides into ${d.label}.`;
    }
  }
  const working = forges.some((f) => f.crafting || f.inQ.length > 0 || f.outQ.length > 0);
  if (!working) return `Next: press 1 and drag a belt from the ${needSpec ? 'spec desk' : 'indexer'}\u2019s arrow side into ${forges[0].label}.`;
  return `Next: drag a belt from ${forges[0].label}\u2019s arrow side into the users.`;
}

const BELT_KINDS = new Set<BuildingKind>(['belt', 'breaker', 'gate']);

export function stateSummary(host: CommandHost) {
  const w = host.world;
  const g = w.goalProgress();
  const list = [...w.buildings.values()].filter((b) => b.kind !== 'belt' && !(b.kind === 'nest' && !w.revealed(b.x + 1, b.y + 1)) && (w.revealed(b.x, b.y) || BUILDINGS[b.kind].placeable));
  const belts = [...w.buildings.values()].filter((b) => b.kind === 'belt').length;
  const u = usersCenter(w);
  const nearGlitch = w.glitches.reduce((m, gl) => Math.min(m, Math.hypot(gl.x - u.x, gl.y - u.y)), Infinity);
  const patches: { type: string; x: number; y: number }[] = [];
  for (const f of w.level.features) {
    if (f.t !== 'patch') continue;
    if (!w.revealed(Math.round(f.x), Math.round(f.y))) continue;
    patches.push({ type: f.res === 'rich' ? 'context (rich)' : f.res, x: Math.round(f.x), y: Math.round(f.y) });
  }
  if (w.monolithTapped) {
    const m = w.level.features.find((f) => f.t === 'monolith');
    if (m) patches.push({ type: 'context (rich, from the monolith)', x: m.x + 3, y: m.y + 3 });
  }
  return {
    time: clock(w.seconds),
    level: w.level.name,
    goal: { label: g.label, progress: `${g.value}/${g.target}`, done: g.done, note: g.sub },
    timeLeft: w.level.timeLimit ? clock(w.level.timeLimit - w.seconds) : null,
    budget: Math.floor(w.credits),
    trust: Math.round(w.trust),
    insight: Math.floor(w.insight),
    compute: `${Math.round(w.computeDemand)}/${Math.round(w.computeSupply)} used`,
    ratePerMin: w.rate(),
    buildings: list.slice(0, 90).map((b) => ({
      label: b.label, kind: b.kind, x: b.x, y: b.y, size: b.w, facing: DIR_NAMES[b.dir], status: visibleStatus(w, b),
      ...(b.kind === 'checkpoint' && b.cooldown > 0 ? { recharging: Math.ceil(b.cooldown) } : {}),
    })),
    belts,
    problems: problems(w),
    openIncidents: w.incidents.filter((i) => !i.contained && i.resolvedAt < 0).slice(-6).map((i) => ({ cause: i.cause, origin: i.origin, machines: i.affected })),
    glitches: w.glitches.length,
    nearestGlitchToUsers: Number.isFinite(nearGlitch) ? Math.round(nearGlitch) : null,
    approvals: w.approvals.filter((a) => !a.done).map((a) => ({ what: a.label, answerIn: Math.max(0, Math.ceil((a.resolveAt - w.tick) / 30)) })),
    research: TECHS.filter((t) => w.level.techs.includes(t.id) && !w.techs.has(t.id)).map((t) => ({ id: t.id, name: t.name, cost: t.cost, effect: t.blurb })),
    resources: patches,
    zones: w.zones.map((z) => ({ name: z.name, owner: z.owner, state: z.state })),
    monolith: [...w.buildings.values()].some((b) => b.kind === 'monolith') ? (w.revealed(w.level.features.find((f) => f.t === 'monolith')?.x ?? 0, w.level.features.find((f) => f.t === 'monolith')?.y ?? 0) ? 'locked (found)' : 'locked (not found yet)') : w.monolithTapped ? 'open' : 'none',
    allowedBuildings: w.level.allowed,
    blueprint: host.blueprint ? `${host.blueprint.items.length} pieces` : null,
    cursor: host.cursor ?? null,
  };
}

/** Run a named tool with JSON-ish args. Used by voice and the Operator API. */
export function runTool(host: CommandHost, name: string, args: Record<string, unknown>): CmdResult {
  const s = (k: string) => (typeof args[k] === 'string' ? (args[k] as string) : undefined);
  const arr = (k: string) => (Array.isArray(args[k]) ? (args[k] as unknown[]).map(String) : typeof args[k] === 'string' ? [args[k] as string] : []);
  switch (name) {
    case 'get_state':
      return { ok: true, message: 'state', data: stateSummary(host) };
    case 'build':
      return cmdBuild(host, { kind: s('kind') ?? '', count: typeof args.count === 'number' ? args.count : undefined, near: s('near'), facing: s('facing'), resource: s('resource') });
    case 'connect':
      return cmdConnect(host, { from: s('from') ?? '', to: s('to') ?? '', port: s('port') });
    case 'remove':
      return cmdRemove(host, { targets: arr('targets') });
    case 'rotate':
      return cmdRotate(host, { target: s('target') ?? '', facing: s('facing') ?? '' });
    case 'research':
      return cmdResearch(host, { tech: s('tech') ?? '' });
    case 'rollback':
      return cmdRollback(host, { checkpoint: s('checkpoint') });
    case 'patch':
      return cmdPatch(host, { targets: arr('targets') });
    case 'reset_breakers':
      return cmdResetBreakers(host);
    case 'ask_permission':
      return cmdAsk(host, { what: s('what') ?? '' });
    case 'act_without_permission':
      return cmdAct(host, { what: s('what') ?? '', near: s('near') });
    case 'claim':
      return cmdClaim(host, { target: s('target') ?? '' });
    case 'set_speed': {
      const sp = Number(args.speed);
      if (![0, 1, 2, 4].includes(sp)) return { ok: false, message: 'Speed is 0, 1, 2 or 4.' };
      host.setSpeed?.(sp);
      return { ok: true, message: sp ? `Speed ${sp}x.` : 'Paused.' };
    }
    case 'focus': {
      const p = resolvePoint(host, s('target'));
      if (!p) return { ok: false, message: `I can't find "${s('target')}".` };
      host.focus?.(p.x, p.y);
      return { ok: true, message: 'Camera moved.' };
    }
    case 'stamp_blueprint':
      return cmdStamp(host, { near: s('near') });
    case 'undo': {
      const r = host.world.apply({ type: 'undo' }, host.src);
      return { ok: r.ok, message: r.ok ? 'Undone.' : r.reason ?? '' };
    }
    case 'test_plan': {
      const r = host.world.apply({ type: 'testPlan' }, host.src);
      return { ok: r.ok, message: r.detail ?? r.reason ?? '' };
    }
    case 'submit_plan': {
      const r = host.world.apply({ type: 'submitPlan' }, host.src);
      return { ok: r.ok, message: r.detail ?? r.reason ?? '' };
    }
    default:
      return { ok: false, message: `Unknown tool ${name}.` };
  }
}

export const DIRS4: Dir[] = [0, 1, 2, 3];
export { DX, DY };
